import { Sprite, Texture } from 'pixi.js';
import LAYOUT from './layout.json';

/**
 * Card Flip drawn from its real-3D render (scripts/art/render/cards.html), seen straight on:
 * the cream face, the teal back and the four suits, all from one camera, so they lay over
 * each other as one card. The ranks are written by the scene.
 */

type Box = [number, number, number, number];
const BOXES = LAYOUT.boxes as unknown as Record<string, Box>;
const [CX, CY] = LAYOUT.scene.centre as unknown as [number, number];
const CARD_PX: number = LAYOUT.scene.cardPx;

export const CHIP_COLOURS = ['red', 'blue', 'green'] as const;

export const CARDS_PIECES = [
  'card-front',
  'card-back',
  'suit-s',
  'suit-h',
  'suit-d',
  'suit-c',
  ...CHIP_COLOURS.map((c) => `chip-${c}` as const),
  'sparkle',
  'glow',
] as const;
export type CardsPiece = (typeof CARDS_PIECES)[number];

export type SuitLetter = 'S' | 'H' | 'D' | 'C';

export function suitPiece(suit: SuitLetter): CardsPiece {
  return `suit-${suit.toLowerCase()}` as CardsPiece;
}

/** A rendered layer in the scene's card units, for a card `cardH` tall, centred on the card's middle. */
export function placeOnCard(tex: Texture, piece: CardsPiece, cardH: number): Sprite {
  const [x, y, w, h] = BOXES[piece]!;
  const k = cardH / CARD_PX;
  const s = new Sprite(tex);
  s.position.set((x - CX) * k, (y - CY) * k);
  s.width = w * k;
  s.height = h * k;
  return s;
}

/**
 * A suit at (x, y) on the card, `scale` of its size in the middle of the card (1). Anchored on
 * the point it was rendered about, so a corner suit sits where the middle one's centre would.
 */
export function placeSuit(tex: Texture, suit: SuitLetter, cardH: number, x: number, y: number, scale: number): Sprite {
  const [bx, by, w, h] = BOXES[suitPiece(suit)]!;
  const k = (cardH / CARD_PX) * scale;
  const s = new Sprite(tex);
  s.anchor.set((CX - bx) / w, (CY - by) / h);
  s.position.set(x, y);
  s.width = w * k;
  s.height = h * k;
  return s;
}

/** A soft, blurred rounded-rectangle shadow, drawn once: light and shade have no shape to get wrong. */
export function makeShadowTexture(w: number, h: number, r: number, blur: number): Texture {
  const pad = blur * 3;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(w + pad * 2);
  canvas.height = Math.ceil(h + pad * 2);
  const ctx = canvas.getContext('2d');
  if (!ctx) return Texture.EMPTY;
  ctx.filter = `blur(${blur}px)`;
  ctx.fillStyle = 'rgba(0,0,0,1)';
  ctx.beginPath();
  ctx.roundRect(pad, pad, w, h, r);
  ctx.fill();
  return Texture.from(canvas);
}
